import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Category', minWidth: 220, maxWidth: 520, defaultWidth: 300 },
  { key: 'slug', label: 'Slug', minWidth: 180, maxWidth: 420, defaultWidth: 240 },
  { key: 'count', label: 'Products', minWidth: 100, maxWidth: 200, defaultWidth: 120 },
  { key: 'parent', label: 'Parent', minWidth: 160, maxWidth: 320, defaultWidth: 190 },
  { key: 'display', label: 'Display', minWidth: 120, maxWidth: 220, defaultWidth: 140 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 320, defaultWidth: 200 },
];

const defaultForm = {
  name: '',
  slug: '',
  description: '',
  parent_id: '',
};

const WooCommerceCategoriesPage = () => {
  const { fetchWithAuth } = useAuth();
  const [sites, setSites] = React.useState([]);
  const [sitesLoading, setSitesLoading] = React.useState(false);
  const [selectedSiteId, setSelectedSiteId] = React.useState('');
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState(defaultForm);
  const [dialogError, setDialogError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.woocommerce-categories',
  });

  const loadSites = React.useCallback(async () => {
    setSitesLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/sites`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce sites');
      }
      const payload = await resp.json();
      const nextSites = Array.isArray(payload.items) ? payload.items : [];
      setSites(nextSites);
      setSelectedSiteId((current) => (
        current && nextSites.some((site) => site.id === current) ? current : nextSites[0]?.id || ''
      ));
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce sites');
    } finally {
      setSitesLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const loadCategories = React.useCallback(async () => {
    if (!selectedSiteId) {
      setCategories([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('site_id', selectedSiteId);
      if (search.trim()) {
        params.set('q', search.trim());
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/categories?${params.toString()}`);
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to load WooCommerce categories');
      }
      const payload = await resp.json();
      setCategories(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      setError(err.message || 'Failed to load WooCommerce categories');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, search, selectedSiteId]);

  React.useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleSync = async () => {
    if (!selectedSiteId) {
      return;
    }
    setSyncing(true);
    setError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/categories/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_id: selectedSiteId }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to sync categories');
      }
      await loadCategories();
    } catch (err) {
      setError(err.message || 'Failed to sync categories');
    } finally {
      setSyncing(false);
    }
  };

  const resetDialog = () => {
    setForm(defaultForm);
    setDialogError(null);
    setDialogOpen(false);
  };

  const updateForm = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!selectedSiteId) {
      setDialogError('Select a site first.');
      return;
    }
    if (!form.name.trim()) {
      setDialogError('Name is required.');
      return;
    }
    setCreating(true);
    setDialogError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/woocommerce/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSiteId,
          name: form.name.trim(),
          slug: form.slug.trim(),
          description: form.description.trim(),
          parent_id: form.parent_id || undefined,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to create category');
      }
      await loadCategories();
      resetDialog();
    } catch (err) {
      setDialogError(err.message || 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  const categoryById = React.useMemo(() => {
    const map = new Map();
    categories.forEach((category) => map.set(category.id, category));
    return map;
  }, [categories]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="WooCommerce Site"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          sx={{ minWidth: 260 }}
          disabled={sitesLoading || sites.length === 0}
        >
          {sites.length === 0 ? (
            <MenuItem value="" disabled>
              {sitesLoading ? 'Loading…' : 'No sites'}
            </MenuItem>
          ) : (
            sites.map((site) => (
              <MenuItem key={site.id} value={site.id}>
                {site.name || site.url}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          fullWidth
          placeholder="Search categories"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={handleSync}
            disabled={!selectedSiteId || syncing}
          >
            {syncing ? 'Syncing…' : 'Sync Categories'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={!selectedSiteId}
          >
            Add Category
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadCategories} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
            <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((column) => (
                  <ResizableHeaderCell
                    key={column.key}
                    column={column}
                    getWidth={getWidth}
                    onResizeStart={startColumnResize}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading categories..." />
              ) : categories.length === 0 ? (
                <TableEmptyRow colSpan={TABLE_COLUMNS.length} label="No categories found." />
              ) : (
                categories.map((category) => {
                  const parent = category.parent_id ? categoryById.get(category.parent_id) : null;
                  return (
                    <TableRow key={category.id || category.external_id} hover>
                      <TableCell>
                        <Stack spacing={0}>
                          <Typography variant="subtitle2">{category.name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {category.external_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap title={category.slug}>
                          {category.slug || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>{category.count ?? 0}</TableCell>
                      <TableCell>{parent?.name || category.parent_external_id || '—'}</TableCell>
                      <TableCell>{category.display || '—'}</TableCell>
                      <TableCell>{category.updated_at ? new Date(category.updated_at).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={resetDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add WooCommerce Category</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleCreate} sx={{ display: 'grid', gap: 1.5, pt: 0.5 }}>
            <TextField label="Name" value={form.name} onChange={updateForm('name')} fullWidth required />
            <TextField label="Slug" value={form.slug} onChange={updateForm('slug')} fullWidth />
            <TextField
              select
              label="Parent Category"
              value={form.parent_id}
              onChange={updateForm('parent_id')}
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={form.description}
              onChange={updateForm('description')}
              multiline
              minRows={3}
              fullWidth
            />
            {dialogError ? <Alert severity="error">{dialogError}</Alert> : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialog} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WooCommerceCategoriesPage;
